import type { ReactNode } from "react";
import {
  Plane,
  Pencil,
  Trash2,
  ArrowRight,
  Bus,
  ExternalLink,
  Ticket,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { FlightSegment, ItemSegment } from "@/lib/types";
import AirlineLogo from "../unified/AirlineLogo";
import PriceHistorySparkline from "../unified/PriceHistorySparkline";
import {
  ITEM_ICON,
  ITEM_LABEL,
  formatTime,
  formatDuration,
  formatPrice,
  arrivesNextDay,
  VERDICT_META,
} from "./itinerary-utils";

interface FlightRowProps {
  seg: FlightSegment;
  /**
   * Drop every affordance and keep only the facts. Used by the printed
   * itinerary, where "Pick a flight" is an invitation nobody can accept and
   * the date-options control is a button on a piece of paper.
   */
  readOnly?: boolean;
  /**
   * The ONE control for everything that can change about this flight: other
   * flights, other dates, price history, trade-offs. Rendered at the foot of the
   * card so the chosen flight stays the thing you read first.
   */
  footer?: ReactNode;
  /** Travellers on the trip, for a leg with no booking to say how many it covers. */
  partySize?: number | null;
}

/**
 * An endpoint of the leg: the CITY is the thing a traveller reads, the airport
 * code is the thing they need at the kerb. City first, code as a mono tag
 * beside it — and the code alone when we don't know the city, never a blank.
 */
function Endpoint({ city, code }: { city: string | null; code: string }) {
  // getAirportInfo returns the code as the "city" for airports it doesn't
  // know, so an equal pair carries no extra information — don't print it twice.
  const label = city && city.toUpperCase() !== code.toUpperCase() ? city : null;
  if (!label) return <span className="font-mono font-semibold">{code}</span>;
  return (
    <span className="inline-flex items-baseline gap-1">
      <span className="font-semibold text-foreground">{label}</span>
      <span className="font-mono text-xs text-muted-foreground">{code}</span>
    </span>
  );
}

/**
 * What we hold on record for a booked leg (B4), or null when the leg is merely
 * watched. Every figure is a pass-through of what the traveller told us: the
 * party total is only stated when the basis SAYS the amount is per person, so
 * we can never turn one traveller's fare into a fabricated family total.
 */
function bookingFacts(
  seg: FlightSegment,
): { statusLabel: string; bits: string[] } | null {
  const paid = seg.pricePaid != null && Number.isFinite(seg.pricePaid) ? seg.pricePaid : null;
  const ref = seg.bookingRef ? seg.bookingRef.trim() : "";
  const status = seg.bookingStatus ?? null;
  const booked = status === "booked" || status === "held";
  if (!booked && !ref && paid == null) return null;

  const pax = seg.passengers != null && seg.passengers > 0 ? seg.passengers : null;
  const people = (n: number) => n + (n === 1 ? " traveler" : " travelers");
  const money = (n: number) => formatPrice(n) ?? "";
  const bits: string[] = [];

  if (paid != null) {
    if (seg.priceBasis === "per_person") {
      bits.push(money(paid) + " per person");
      if (pax && pax > 1) bits.push(money(paid * pax) + " for " + people(pax));
    } else if (seg.priceBasis === "total") {
      bits.push(money(paid) + " total" + (pax ? " for " + people(pax) : ""));
    } else {
      // No basis recorded — state the amount and claim nothing about what it covers.
      bits.push(money(paid) + " paid");
    }
  } else if (pax) {
    bits.push(people(pax));
  }
  if (status === "held" && seg.quotedTotal != null && Number.isFinite(seg.quotedTotal)) {
    bits.push(money(seg.quotedTotal) + " quoted, not paid");
  }
  if (seg.seats && seg.seats.length > 0) {
    bits.push((seg.seats.length === 1 ? "seat " : "seats ") + seg.seats.join(", "));
  }
  if (ref) bits.push("confirmation " + ref.toUpperCase());

  return { statusLabel: status === "held" ? "Held" : "Booked", bits };
}

/** "clock back 2h" — what the old "-2h" pill meant, said in words. */
export function clockChange(
  mins: number | null | undefined,
): { label: string; title: string } | null {
  if (mins == null || mins === 0) return null;
  const abs = Math.abs(mins);
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  const amount = (h > 0 ? h + "h" : "") + (m > 0 ? (h > 0 ? " " : "") + m + "m" : "");
  const back = mins < 0;
  return {
    label: "clock " + (back ? "back " : "forward ") + amount,
    title:
      "Arrival time is local. The clock goes " +
      (back ? "back " : "forward ") +
      amount +
      " between the two cities.",
  };
}

/** What the headline fare covers: "per person", plus the party figure when there is one. */
export function perPersonNote(
  seg: Pick<FlightSegment, "price" | "passengers">,
  booked: boolean,
  partySize?: number | null,
): string {
  const n = seg.passengers ?? partySize ?? null;
  const pax = n != null && n > 1 ? n : null;
  const base = booked ? "today · per person" : "per person";
  if (pax && seg.price != null) {
    return base + " · " + (formatPrice(seg.price * pax) ?? "") + " for " + pax;
  }
  return base;
}

export function FlightRow({ seg, readOnly = false, footer, partySize }: FlightRowProps) {
  const dep = formatTime(seg.departLocal);
  const arr = formatTime(seg.arriveLocal);
  const nextDay = arrivesNextDay(seg.departDate, seg.arriveDate);
  const dur = formatDuration(seg.durationMinutes);
  const tz = clockChange(seg.tzShiftMinutes);
  const price = formatPrice(seg.price);
  const verdict = seg.verdict ? VERDICT_META[seg.verdict.status] : null;
  const booking = bookingFacts(seg);

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-card px-3 py-2.5 shadow-paper">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        {/* One gutter, one mark (D5): the carrier's own logo when we know the
            carrier, the generic plane only when we don't. AirlineLogo falls
            back to an IATA monogram by itself if the image 404s. */}
        {seg.airline ? (
          <AirlineLogo iata={seg.airline} logoUrl={seg.logoUrl} size={28} />
        ) : (
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-[hsl(var(--primary))]/[0.08] text-[hsl(var(--accent-foreground))]">
            <Plane className="h-3.5 w-3.5 -rotate-45" aria-hidden="true" />
          </span>
        )}
        <span className="text-sm font-semibold">
          {seg.airlineName || seg.airline || "Flight"}
          {seg.flightNumber && (
            <span className="ml-1 font-mono font-normal text-muted-foreground">
              {seg.flightNumber}
            </span>
          )}
        </span>
        {seg.legRole && (
          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {seg.legRole}
          </span>
        )}
        {/* A booked leg must never read like a fare we are merely watching. */}
        {booking && (
          <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-emerald-800 ring-1 ring-inset ring-emerald-600/25">
            <Ticket className="h-3 w-3" aria-hidden="true" />
            {booking.statusLabel}
          </span>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
        <Endpoint city={seg.originCity} code={seg.origin} />
        {dep && <span className="tabular-nums text-muted-foreground">{dep}</span>}
        <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
        <Endpoint city={seg.destinationCity} code={seg.destination} />
        {arr && (
          <span className="tabular-nums text-muted-foreground">
            {arr}
            {nextDay && (
              <sup className="ml-0.5 font-semibold text-amber-600">+1</sup>
            )}
          </span>
        )}
        <span className="mx-1 h-3 w-px bg-border" aria-hidden="true" />
        {dur && <span className="text-xs tabular-nums text-muted-foreground">{dur}</span>}
        {tz && (
          <span className="text-xs text-muted-foreground" title={tz.title}>
            {tz.label}
          </span>
        )}
        {seg.stops != null && (
          <span className="text-xs text-muted-foreground">
            {seg.stops === 0
              ? "Nonstop"
              : seg.stops + (seg.stops === 1 ? " stop" : " stops")}
          </span>
        )}
        {price && (
          <span className="ml-auto flex flex-col items-end leading-tight">
            <span className="text-sm font-semibold tabular-nums text-foreground">
              {price}
            </span>
            {/* Never a bare figure. With a party it says what the number covers
                and what the party pays; on a booked leg it is TODAY's fare, not
                what was paid, and says so. */}
            <span className="text-[10px] text-muted-foreground">
              {perPersonNote(seg, booking != null, partySize)}
            </span>
          </span>
        )}
      </div>

      {/* The fare's own history (T4: facts from the summary, the line is only
          a drawing). Renders nothing under two points, so a freshly-tracked
          flight shows no empty chart. */}
      <PriceHistorySparkline history={seg.priceHistory} stretch />

      {booking && booking.bits.length > 0 && (
        <div className="flex flex-wrap items-baseline gap-x-1 border-t border-border/70 pt-2 text-xs text-muted-foreground">
          {/* T1 — a paid price comes from the traveller, not from a scrape. */}
          <span className="font-medium text-foreground">On record:</span>
          <span>{booking.bits.join(" · ")}</span>
          <span className="text-[11px]">(you told us)</span>
        </div>
      )}

      {seg.transfer && (
        <div className="flex flex-wrap items-center gap-2 border-t border-border/70 pt-2 text-xs text-muted-foreground">
          <Bus className="h-3.5 w-3.5" aria-hidden="true" />
          <span className="font-medium text-foreground">{seg.transfer.name}</span>
          <span>· {formatDuration(seg.transfer.minutes) || seg.transfer.mode}</span>
          {seg.transfer.costFromUsd > 0 && (
            <span>
              · from <span className="font-mono">{formatPrice(seg.transfer.costFromUsd)}</span>
            </span>
          )}
          {seg.transfer.bookingUrl && (
            <a
              href={seg.transfer.bookingUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 font-medium text-[hsl(var(--primary))] hover:underline"
            >
              Book <ExternalLink className="h-3 w-3" aria-hidden="true" />
            </a>
          )}
        </div>
      )}

      {seg.verdict && verdict && (
        <div
          className={
            "flex items-start gap-1.5 rounded-md border px-2 py-1 text-xs " +
            verdict.tone
          }
        >
          <span className={"mt-1 h-1.5 w-1.5 shrink-0 rounded-full " + verdict.dot} />
          <span>{seg.verdict.detail}</span>
        </div>
      )}

      {!readOnly && footer}
    </div>
  );
}

interface ItemRowProps {
  seg: ItemSegment;
  onEdit: (seg: ItemSegment) => void;
  onDelete: (seg: ItemSegment) => void;
  deleting?: boolean;
  /** Facts only — no edit/delete. The printed itinerary. */
  readOnly?: boolean;
}

export function ItemRow({
  seg,
  onEdit,
  onDelete,
  deleting,
  readOnly = false,
}: ItemRowProps) {
  const Icon = ITEM_ICON[seg.item_type] ?? Bus;
  const start = formatTime(seg.start_time);
  const end = formatTime(seg.end_time);
  const place = seg.location || seg.city;

  return (
    <div
      className={
        "group flex items-start gap-3 rounded-lg border px-3 py-2.5 shadow-paper " +
        (seg.is_anchor
          ? "border-amber-200 bg-amber-50/40"
          : "border-border bg-card")
      }
    >
      <span
        className={
          "flex h-7 w-7 shrink-0 items-center justify-center rounded-full " +
          (seg.is_anchor
            ? "bg-amber-100 text-amber-600"
            : "bg-muted text-muted-foreground")
        }
      >
        <Icon
          className={"h-3.5 w-3.5" + (seg.is_anchor ? " fill-amber-400" : "")}
          aria-hidden="true"
        />
      </span>

      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-sm font-semibold text-foreground">{seg.title}</span>
          {seg.is_anchor && (
            <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-700">
              Anchor
            </span>
          )}
          <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
            {ITEM_LABEL[seg.item_type] ?? seg.item_type}
          </span>
        </div>
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
          {start && (
            <span className="font-medium tabular-nums text-foreground/80">
              {start}
              {end && " – " + end}
            </span>
          )}
          {place && <span>{place}</span>}
          {seg.city && place !== seg.city && <span>{seg.city}</span>}
          {seg.added_by?.display_name && (
            <span className="italic">added by {seg.added_by.display_name}</span>
          )}
        </div>
        {seg.notes && (
          <p className="mt-1 text-xs leading-snug text-muted-foreground">
            {seg.notes}
          </p>
        )}
      </div>

      {!readOnly && (
      <div className="flex shrink-0 items-center gap-0.5 opacity-60 transition-opacity group-hover:opacity-100 focus-within:opacity-100">
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7"
          aria-label={"Edit " + seg.title}
          onClick={() => onEdit(seg)}
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          variant="ghost"
          size="icon"
          className="h-7 w-7 text-destructive hover:text-destructive"
          aria-label={"Delete " + seg.title}
          disabled={deleting}
          onClick={() => onDelete(seg)}
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
      )}
    </div>
  );
}
